import * as React from 'react';
import isNil from 'lodash/isNil';
import moment from 'moment';

import ActionButton from 'components/buttons/action-button/ActionButton';
import Tooltip from 'components/Tooltip';
import NotableLikes from '../notable-likes/NotableLikes';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum } from 'utils/helper/common';

import type { InstagramPostsReportingLikersInfo, ReportingSummaryInfluencersMap } from 'modules/reporting/types';
import type { ContentCardItem } from 'components/cards/content-card/types';
import type { NotableLikesItem } from '../notable-likes/types';

applyTranslations(require('./instagram-posts-notable-likes.i18n.json'));

import './instagram-posts-notable-likes.scss';

type Props = {
  posts: Array<ContentCardItem>,
  likersInfo: InstagramPostsReportingLikersInfo,
  onNotableLikesDataRequest: () => void,
  influencersMapById: ReportingSummaryInfluencersMap,
  onNoPostClick: () => void
}

const InstagramPostsNotableLikes = ({
  posts,
  likersInfo,
  influencersMapById,
  onNotableLikesDataRequest,
  onNoPostClick
}: Props) => {
  const { totalLikes, notableLikes, lastRefreshDate } = likersInfo || {};
  const isDataAvailable = (!isNil(totalLikes) || !isNil(notableLikes)) && !isNil(lastRefreshDate);

  const getNotableLikers = (): Array<NotableLikesItem> => posts.reduce<Array<NotableLikesItem>>((filtered, post, index) => {
    if (post.likers?.notableLikers.length) {
      filtered.push({
        creatorFollower: post.likers.notableLikers[0],
        channel: 'instagram',
        contentCard: {
          item: post,
          index,
          channel: 'instagram',
          creatorProfile: influencersMapById[post.influencerProfileId]
        }
      });
    }
    return filtered;
  }, []);

  if (!isDataAvailable) {
    return (
      <div className="instagram-posts-notable-likes instagram-posts-notable-likes--request">
        <div className="instagram-posts-notable-likes__disclaimer">
          {__t('instagram_post_notable_likes.notable_likes_disclaimer')}
        </div>
        <Tooltip
          message={__t('instagram_post_notable_likes.data_request_timeframe')}
        >
          <ActionButton
            onClick={() => onNotableLikesDataRequest()}
            color="secondary"
          >
            {__t('instagram_post_notable_likes.request_data_btn')}
          </ActionButton>
        </Tooltip>
      </div>
    );
  }

  const notableLikersCards = getNotableLikers();

  return (
    <React.Fragment>
      <div className="instagram-posts-notable-likes">
        <div className="instagram-posts-notable-likes__current">
          <div className="instagram-posts-notable-likes__percentage">
            <div className="instagram-posts-notable-likes__percentage-label">
              {__t('instagram_post_notable_likes.notable_likes')}
              <Tooltip
                message={__t('instagram_post_notable_likes.notable_likes_tooltip')}
                width="long"
                showQuestionMark
                classNameWrapper="instagram-posts-notable-likes__tooltip"
              />
            </div>
            <div className="instagram-posts-notable-likes__percentage-value">
              {isNil(notableLikes) ? 'n/a' : `${notableLikes.toFixed(2)}%`}
            </div>
          </div>
          <div className="instagram-posts-notable-likes__absolute">
            <div className="instagram-posts-notable-likes__absolute-label">
              {__t('instagram_post_notable_likes.total_notable_likes')}
              <Tooltip
                message={__t('instagram_post_notable_likes.total_notable_likes_tooltip')}
                width="long"
                showQuestionMark
                classNameWrapper="instagram-posts-notable-likes__tooltip"
              />
            </div>
            <div className="instagram-posts-notable-likes__absolute-value">
              {isNil(totalLikes) ? 'n/a' : abbrNum(totalLikes)}
            </div>
          </div>
        </div>
        <div className="instagram-posts-notable-likes__update">
          <div className="instagram-posts-notable-likes__update_link_container">
            <Tooltip
              message={__t('instagram_post_notable_likes.data_request_timeframe')}
              classNameWrapper="instagram-posts-notable-likes__update_link"
              onClick={() => onNotableLikesDataRequest()}
            >
              {__t('instagram_post_notable_likes.request_an_update_link')}
            </Tooltip>
            {__t('instagram_post_notable_likes.of_data')}
          </div>
          <div className="instagram-posts-notable-likes__last_updated_date">
            {__t('instagram_post_notable_likes.last_update',
              { lastRefreshDate: moment(lastRefreshDate || '').format('DD/MM/YYYY') })}
          </div>
        </div>
      </div>
      { notableLikersCards.length > 0 && (
        <div className="instagram-posts-notable-likes__notable-creators">
          <div className="instagram-posts-notable-likes__notable-creators-label">
            {__t('instagram_post_notable_likes.notable_creator_label')}
            <Tooltip
              message={__t('instagram_post_notable_likes.notable_creator_tooltip')}
              width="long"
              showQuestionMark
              classNameWrapper="instagram-posts-notable-likes__tooltip"
            />
          </div>
          <NotableLikes
            key="instagram-posts"
            cards={notableLikersCards}
            posts={posts}
            type="instagram_post"
            onNoPostClick={onNoPostClick}
          />
        </div>
      )
      }
    </React.Fragment>
  );
};

export default InstagramPostsNotableLikes;
